import React, { useState, useEffect, useRef } from 'react';
import { FaBell } from 'react-icons/fa';
import axios from 'axios';
import useWebSocket from '@/hooks/useWebSocket';
import events from '@/constants/websocketEvents';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const dropdownRef = useRef(null);

  // Fonction pour nettoyer le texte HTML
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Fonction pour récupérer les notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications/global-notifications');
      const notifications = response.data;

      // Récupérer les notifications lues depuis le localStorage
      const readNotifications = JSON.parse(localStorage.getItem('readNotifications')) || [];

      // Marquer les notifications non lues et compter le nombre de non lues
      const unread = notifications.filter(
        (notif) => !readNotifications.includes(notif.id)
      );
      setUnreadCount(unread.length);  // Met à jour le compteur
      setNotifications(notifications);
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications :', error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Marquer une notification comme lue et ouvrir le popup
  const handleNotificationClick = (notif) => {
    setSelectedNotification(notif);

    // Ajouter l'ID de la notification au localStorage pour la marquer comme lue
    const readNotifications = JSON.parse(localStorage.getItem('readNotifications')) || [];
    if (!readNotifications.includes(notif.id)) {
      localStorage.setItem('readNotifications', JSON.stringify([...readNotifications, notif.id]));
    }

    // Mettre à jour le compteur de notifications non lues
    setUnreadCount((prevCount) => prevCount - 1);
  };

  useWebSocket(events.NOTIFICATION_CREATED, fetchNotifications);

  return (
    <div className="relative" ref={dropdownRef}>
      <button className="relative" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
        <FaBell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border rounded shadow-lg z-10">
          <ul>
            {notifications.length === 0 ? (
              <li className="p-2 text-gray-500">Aucune notification</li>
            ) : (
              notifications.map((notif) => {
                const isRead = JSON.parse(localStorage.getItem('readNotifications') || '[]').includes(notif.id);
                return (
                  <li
                    key={notif.id}
                    className={`p-2 border-b cursor-pointer ${!isRead ? 'font-bold' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <span className='text-sm text-gray-700'>{stripHtml(notif.message)}</span>
                    <span className="text-xs text-gray-400 block">{formatDate(notif.startDate)}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}

      {selectedNotification && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h2 className="text-xl text-gray-700 font-bold mb-4">{selectedNotification.name}</h2>
            <p className='text-gray-400'>{stripHtml(selectedNotification.message)}</p>
            <p className="text-sm text-gray-500 mt-2">{formatDate(selectedNotification.startDate)}</p>
            <button
              className="mt-4 bg-primary hover:bg-primary/80 text-white font-bold py-2 px-4 rounded-xl"
              onClick={() => setSelectedNotification(null)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
